import { useState } from 'react'
import { FileDownload } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { useAppDispatch } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackTimelineEvent } from '@/analytics/utils/timeline-analytics'
import { downloadTimelineAssetExcel } from '../fetch/download-timeline-asset-excel'

interface TimelineAssetDownloadButtonProps {
  assetId: string
  compact?: boolean
}

/**
 * Downloads the living timeline as an Excel file.
 */
export default function TimelineAssetDownloadButton(props: TimelineAssetDownloadButtonProps) {
  const { assetId, compact = false } = props
  const dispatch = useAppDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const title = isLoading ? 'Download in progress for Timeline' : 'Download Timeline'

  const handleDownload = async () => {
    if (isLoading) return
    setIsLoading(true)
    trackTimelineEvent(AnalyticsEvent.TimelineAssetDownloadClicked, { timelineAssetId: assetId })
    try {
      await downloadTimelineAssetExcel(assetId, 'Timeline.xlsx')
    } catch (error) {
      console.error('Error downloading timeline Excel:', error)
      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'Timeline download failed. Please try again.',
          durationMs: kToastDurationMediumMs,
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  if (compact) {
    return (
      <IconButton
        aria-label={title}
        color="primary"
        icon={<FileDownload />}
        loading={isLoading}
        onClick={handleDownload}
        size="lg"
        title={title}
        variant="plain"
      />
    )
  }

  return <IconButton aria-label={title} icon={<FileDownload />} loading={isLoading} onClick={handleDownload} size="sm" title={title} variant="outlined" />
}
